/**
 * Site step, "Property, street and equipment" (sheet PV-1): the property lines (the open parcel data's, copied in
 * one click, or drawn on the plan), the street's name, distances the installer measured, and the equipment placed
 * on the plan (inverter, disconnects, combiner, battery…), with what kind the plan's "Place" tool puts down.
 */
import { MapPinned, Plus, Trash2 } from "lucide-react";
import type { S } from "../api/client";
import { Alert, Button, Card, Field, NumberInput, Select, TextInput } from "../components/ui";
import { useT } from "../i18n/I18nProvider";
import type { MessageKey } from "../i18n/keys";
import { fromDisplay, toDisplay } from "../lib/permitWorkspace";
import { EQUIPMENT_KINDS, type EquipmentKind } from "./SitePlan";
import type { Ws } from "./PermitWorkspace";

type OpenData = S["PermitOpenData"];

/** The names of the equipment kinds the plan's "Place" tool puts down (SitePlan's EQUIPMENT_KINDS, same values). */
const KIND_KEY: Record<EquipmentKind, MessageKey> = {
  inverter: "permit.surroundings.kind.inverter",
  ac_disconnect: "permit.surroundings.kind.acDisconnect",
  dc_disconnect: "permit.surroundings.kind.dcDisconnect",
  combiner: "permit.surroundings.kind.combiner",
  battery: "permit.surroundings.kind.battery",
  sub_panel: "permit.surroundings.kind.subPanel",
  production_meter: "permit.surroundings.kind.productionMeter",
  ev_charger: "permit.surroundings.kind.evCharger",
  other: "permit.surroundings.kind.other",
};

export function SiteSurroundings({
  ws,
  openData,
  loading,
  equipmentKind,
  onEquipmentKind,
  onPlace,
}: {
  ws: Ws;
  openData: OpenData | undefined;
  loading: boolean;
  equipmentKind: EquipmentKind;
  onEquipmentKind: (k: EquipmentKind) => void;
  onPlace: () => void;
}) {
  const t = useT();
  const kindOptions = EQUIPMENT_KINDS.map((e) => ({ value: e.value, label: t(KIND_KEY[e.value]) }));
  const d = ws.details;
  const off = !ws.canEdit;
  const units = ws.units;
  const imperial = units === "imperial";
  const set = (patch: Partial<typeof d>) => ws.setDetails((x) => ({ ...x, ...patch }));
  const parcel = openData?.parcel;
  const lines = d.property_lines ?? [];
  const distances = d.property_distances ?? [];
  const equipment = d.equipment_locations ?? [];
  const streetHint = openData?.street?.name ?? openData?.address_street ?? undefined;

  const useParcel = () =>
    parcel &&
    set({
      property_lines: parcel.rings.map((r) => r.map((p) => ({ x: p.x, y: p.y }))),
      property_line_source: "parcel",
      property_line_note: `${parcel.source}${parcel.parcel_id ? `, parcel ${parcel.parcel_id}` : ""}; ${parcel.licence}`.slice(0, 300),
    });

  return (
    <Card title={t("permit.surroundings.title")} description={t("permit.surroundings.description")}>
      <div className="stack" data-anchor="property">
        <div className="stack" style={{ gap: 6 }}>
          <b className="small">{t("permit.surroundings.propertyLines")}</b>
          {loading ? (
            <span className="small muted">{t("permit.surroundings.looking")}</span>
          ) : parcel ? (
            <Alert tone="info">
              {parcel.parcel_id
                ? t("permit.surroundings.parcelWithId", { source: parcel.source, parcel: parcel.parcel_id, licence: parcel.licence })
                : t("permit.surroundings.parcel", { source: parcel.source, licence: parcel.licence })}
              {lines.length ? "" : ` ${t("permit.surroundings.parcelUsed")}`}
            </Alert>
          ) : (
            <span className="small muted">
              {openData?.errors.length ? t("permit.surroundings.noParcelWithErrors", { errors: openData.errors.join("; ") }) : t("permit.surroundings.noParcel")}
            </span>
          )}
          <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
            {parcel && (
              <Button size="sm" icon={<MapPinned size={14} />} disabled={off} onClick={useParcel}>
                {lines.length && d.property_line_source === "parcel" ? t("permit.surroundings.reloadParcel") : t("permit.surroundings.useParcel")}
              </Button>
            )}
            {lines.length > 0 && (
              <Button size="sm" variant="ghost" icon={<Trash2 size={14} />} disabled={off} onClick={() => set({ property_lines: [], property_line_source: null, property_line_note: null })}>
                {t("permit.surroundings.clearLines", { count: lines.length })}
              </Button>
            )}
            {lines.length > 0 && (
              <span className="small muted">{d.property_line_source === "parcel" ? t("permit.surroundings.fromParcel") : t("permit.surroundings.drawn")}</span>
            )}
          </div>
        </div>

        <Field label={t("permit.surroundings.street")} hint={openData?.street ? t("permit.surroundings.streetFromOsm") : t("permit.surroundings.streetHint")}>
          <TextInput value={d.street_name ?? ""} disabled={off} maxLength={120} placeholder={streetHint ?? t("permit.surroundings.streetPlaceholder")} onChange={(e) => set({ street_name: e.target.value || null })} />
        </Field>

        <div className="stack" style={{ gap: 6 }} data-anchor="distances">
          <b className="small">{t("permit.surroundings.distances")}</b>
          <span className="small muted">{t("permit.surroundings.distancesHint")}</span>
          {distances.map((row, i) => (
            <div className="pw-list-row" key={i}>
              <Field label={i === 0 ? t("permit.surroundings.what") : ""}>
                <TextInput value={row.label} disabled={off} maxLength={80} placeholder={t("permit.surroundings.whatPlaceholder")} onChange={(e) => set({ property_distances: distances.map((r, j) => (j === i ? { ...r, label: e.target.value } : r)) })} />
              </Field>
              <Field label={i === 0 ? t("permit.surroundings.distance") : ""}>
                <NumberInput
                  value={toDisplay(row.distance_m, units, "long")}
                  disabled={off}
                  suffix={imperial ? "ft" : "m"}
                  min={0}
                  onChange={(v) => set({ property_distances: distances.map((r, j) => (j === i ? { ...r, distance_m: fromDisplay(v, units, "long") ?? 0 } : r)) })}
                />
              </Field>
              <Button size="sm" variant="ghost" icon={<Trash2 size={14} />} aria-label={t("permit.surroundings.removeDistance")} disabled={off} onClick={() => set({ property_distances: distances.filter((_, j) => j !== i) })} />
            </div>
          ))}
          {distances.length < 12 && (
            <div>
              <Button size="sm" icon={<Plus size={14} />} disabled={off} onClick={() => set({ property_distances: [...distances, { label: "", distance_m: 0 }] })}>
                {t("permit.surroundings.addDistance")}
              </Button>
            </div>
          )}
        </div>

        <div className="stack" style={{ gap: 6 }} data-anchor="equipment-locations">
          <b className="small">{t("permit.surroundings.equipmentLocations")}</b>
          <div className="row" style={{ gap: 8, alignItems: "end", flexWrap: "wrap" }}>
            <div style={{ minWidth: 200 }}>
              <Select<EquipmentKind> value={equipmentKind} disabled={off} onChange={onEquipmentKind} options={kindOptions} ariaLabel={t("permit.surroundings.equipmentToPlace")} />
            </div>
            <Button size="sm" variant="primary" icon={<Plus size={14} />} disabled={off || equipment.length >= 24} onClick={onPlace}>
              {t("permit.surroundings.place")}
            </Button>
          </div>
          {equipment.length === 0 ? (
            <span className="small muted">{t("permit.surroundings.notPlaced")}</span>
          ) : (
            equipment.map((e, i) => (
              <div className="pw-list-row" key={i}>
                <Field label={i === 0 ? t("permit.surroundings.equipment") : ""}>
                  <Select<EquipmentKind>
                    value={e.kind}
                    disabled={off}
                    onChange={(k) => set({ equipment_locations: equipment.map((q, j) => (j === i ? { ...q, kind: k } : q)) })}
                    options={kindOptions}
                    ariaLabel={t("permit.surroundings.equipmentKind")}
                  />
                </Field>
                <Field label={i === 0 ? t("permit.surroundings.note") : ""}>
                  <TextInput value={e.note ?? ""} disabled={off} maxLength={120} placeholder={t("permit.surroundings.notePlaceholder")} onChange={(ev) => set({ equipment_locations: equipment.map((q, j) => (j === i ? { ...q, note: ev.target.value || null } : q)) })} />
                </Field>
                <Button size="sm" variant="ghost" icon={<Trash2 size={14} />} aria-label={t("permit.surroundings.removeEquipment")} disabled={off} onClick={() => set({ equipment_locations: equipment.filter((_, j) => j !== i) })} />
              </div>
            ))
          )}
        </div>
      </div>
    </Card>
  );
}
